import { useQuery } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router";
import { useEndpoints } from "../../api/ApiContext";
import type { EarningsBreakdown, EarningsShadow, PublisherEarnings, SiteList, SiteSummary } from "../../api/types";
import {
  Banner,
  Button,
  Card,
  EmptyState,
  Input,
  Money,
  MoneyStat,
  PageHeader,
  QueryView,
  Select,
  StatusPill,
  Table,
} from "../../components/ui";
import { formatLocal } from "../../lib/datetime";
import { isZeroDecimal } from "../../lib/money";
import { useCursorList } from "../../lib/useCursorList";
import { PromotionalEarningsCard } from "./PromotionalEarnings";

// UX-DESIGN.md §3.4 (migration 0057, publisher_earnings.go):
//   GET /v1/earnings             party balances, pending cohorts, held history (G-38)
//   GET /v1/earnings/breakdown   per-site, per-slot rows for a window (G-39)
//   GET /v1/earnings/shadow      CPE evaluation-mode activity (G-39) — a separate
//                                endpoint, rendered in its own section, never
//                                added to any figure above it.
// Recognized, pending and compliance-held are three distinct numbers,
// never summed into one "balance".

function asOf(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

/** A date input ("2026-09-01") → the local midnight instant as RFC 3339, or undefined. */
function dayStart(v: string): string | undefined {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) return undefined;
  const [y, m, d] = v.split("-").map((x) => parseInt(x, 10)) as [number, number, number];
  return new Date(y, m - 1, d).toISOString().replace(/\.\d{3}Z$/, "Z");
}

function useSiteNames() {
  const ep = useEndpoints();
  const list = useCursorList<SiteList, SiteSummary>(["sites"], (cursor) => ep.publisher.listSites(cursor), (p) => p.sites);
  const names = new Map(list.rows.map((s) => [s.site_id, s.domain]));
  return { sites: list.rows, name: (id: string) => names.get(id) ?? id.slice(0, 8) };
}

export function EarningsPage() {
  const [params, setParams] = useSearchParams();
  const siteId = params.get("site") ?? "";
  const from = params.get("from") ?? "";
  const to = params.get("to") ?? "";
  const sites = useSiteNames();
  const [draftFrom, setDraftFrom] = useState(from);
  const [draftTo, setDraftTo] = useState(to);

  function apply(e: FormEvent) {
    e.preventDefault();
    const next: Record<string, string> = {};
    if (siteId) next.site = siteId;
    if (draftFrom) next.from = draftFrom;
    if (draftTo) next.to = draftTo;
    setParams(next);
  }

  const range = { from: dayStart(from), to: dayStart(to), siteId: siteId || null };
  return (
    <div className="stack-lg">
      <PageHeader title="Earnings" description="Live figures from the ledger. Issued statements are the final record." />
      <PartyBalancesCard />
      <PromotionalEarningsCard />
      <Card title="Filter" subtitle="Applies to the per-slot table and the evaluation-mode section. Defaults to the last 30 days, all sites.">
        <form className="row" style={{ alignItems: "flex-end" }} onSubmit={apply} noValidate>
          <div style={{ flex: "1 1 220px" }}>
            <Select
              label="Site"
              value={siteId}
              onChange={(e) => {
                const next = new URLSearchParams(params);
                if (e.target.value) next.set("site", e.target.value);
                else next.delete("site");
                setParams(next);
              }}
              options={[{ value: "", label: "All sites" }, ...sites.sites.map((s) => ({ value: s.site_id, label: s.domain }))]}
            />
          </div>
          <div style={{ flex: "0 1 180px" }}>
            <Input label="From" type="date" value={draftFrom} onChange={(e) => setDraftFrom(e.target.value)} />
          </div>
          <div style={{ flex: "0 1 180px" }}>
            <Input label="To (not including)" type="date" value={draftTo} onChange={(e) => setDraftTo(e.target.value)} />
          </div>
          <Button type="submit" variant="secondary">
            Apply
          </Button>
        </form>
      </Card>
      <BreakdownCard range={range} siteName={sites.name} />
      <ShadowCard range={range} siteName={sites.name} />
    </div>
  );
}

function PartyBalancesCard() {
  const ep = useEndpoints();
  const q = useQuery({ queryKey: ["earnings"], queryFn: () => ep.publisher.getEarnings(), refetchInterval: 60_000 });
  return (
    <Card title="Your balances" subtitle="Across all your sites. Three separate figures — never added together.">
      <QueryView query={q} loadingLabel="Loading balances" lines={4}>
        {(e: PublisherEarnings) => <PartyBalances e={e} />}
      </QueryView>
    </Card>
  );
}

function PartyBalances({ e }: { e: PublisherEarnings }) {
  const debtCurrencies = e.by_currency.filter((r) => !isZeroDecimal(r.debt));
  return (
    <div className="stack">
      <div className="row">
        <StatusPill tone="info" icon="clock">
          Live — not yet final
        </StatusPill>
        <span className="small muted">as of {asOf(e.as_of)} — figures may still change</span>
      </div>
      {e.compliance_resolution_failed ? (
        <Banner tone="error" title="Compliance resolution failed — balance frozen pending legal review">
          Identity verification couldn't be completed, so earnings held pending verification are frozen while the platform's legal review
          decides what happens to them. Contact support for the case.
        </Banner>
      ) : null}
      {e.by_currency.length === 0 ? <p className="muted">No earnings yet. They appear here from the first captured event.</p> : null}
      {e.by_currency.map((r) => (
        <div key={r.currency} className="stack">
          <div className="grid-auto">
            <MoneyStat label="Recognized" value={r.recognized} currency={r.currency} note="Past the audit window; can be paid out, less any holds" />
            <MoneyStat label="Pending" value={r.pending} currency={r.currency} note="Inside the audit/dispute window — estimate, not guaranteed" alwaysFlag />
            {!isZeroDecimal(r.compliance_held) || e.kyc_status !== "cleared" ? (
              <MoneyStat
                label={e.compliance_resolution_failed ? "Frozen pending legal review" : "Held pending verification"}
                value={r.compliance_held}
                currency={r.currency}
                note="Payout-ineligible until identity verification clears"
              />
            ) : null}
          </div>
          <dl className="definition-list">
            <dt>Claimable by a payout now</dt>
            <dd>
              <Money value={r.payout_available} currency={r.currency} />{" "}
              <span className="xs muted">before payout-account checks —</span> <Link to="/publisher/payouts">see what you can request</Link>
            </dd>
            <dt>In payouts already requested</dt>
            <dd>
              <Money value={r.payout_in_flight} currency={r.currency} />
            </dd>
          </dl>
        </div>
      ))}
      {debtCurrencies.length ? (
        <Banner tone="error" title="An amount is owed to the platform">
          <div className="stack-sm">
            {debtCurrencies.map((r) => (
              <span key={r.currency}>
                <Money value={r.debt} currency={r.currency} /> owed.
              </span>
            ))}
            <span>
              <Link to="/publisher/debt-cases">See the cases, their evidence summary and how to appeal</Link>
            </span>
          </div>
        </Banner>
      ) : null}
      {e.by_currency.some((r) => !isZeroDecimal(r.compliance_held)) && !e.compliance_resolution_failed ? (
        <Banner tone="warning" title="Earnings held pending verification">
          You're earning while verification runs, but these funds can't be paid out until it clears. When it does, they move into the normal
          pending → recognized pipeline as a visible event. The dates in the pipeline are counted from capture, but payout eligibility for a
          newly cleared account starts from the clearance date.
        </Banner>
      ) : null}
      {e.pending_cohorts.length ? (
        <Table
          caption="Pending earnings by expected release"
          rows={e.pending_cohorts}
          rowKey={(c) => `${c.currency}-${c.expected_recognition_at}`}
          columns={[
            {
              key: "when",
              header: "Expected to become available",
              cell: (c) => (c.expected_recognition_at ? `${formatLocal(c.expected_recognition_at)} (estimate)` : "Not yet known"),
            },
            { key: "amt", header: "Amount", cell: (c) => <Money value={c.amount} currency={c.currency} alwaysFlag />, numeric: true },
            { key: "n", header: "Captures", cell: (c) => c.captures, numeric: true },
            {
              key: "holds",
              header: "Holds",
              cell: (c) =>
                c.holds.length ? <StatusPill tone="warning">Safety review — may be later</StatusPill> : <span className="muted">Pending no dispute or fraud hold</span>,
            },
          ]}
        />
      ) : null}
      {e.held_history.length ? (
        <Table
          caption="Held-pending-verification history"
          rows={e.held_history}
          rowKey={(h) => `${h.day}-${h.currency}-${h.event}`}
          columns={[
            { key: "day", header: "Day", cell: (h) => new Date(h.day).toLocaleDateString(undefined, { timeZone: "UTC" }) },
            { key: "ev", header: "Event", cell: (h) => (h.event === "held" ? "Held pending verification" : "Reversed") },
            { key: "amt", header: "Amount", cell: (h) => <Money value={h.amount} currency={h.currency} />, numeric: true },
          ]}
        />
      ) : null}
    </div>
  );
}

interface Range {
  from?: string;
  to?: string;
  siteId: string | null;
}

function BreakdownCard({ range, siteName }: { range: Range; siteName: (id: string) => string }) {
  const ep = useEndpoints();
  const q = useQuery({ queryKey: ["earnings-breakdown", range], queryFn: () => ep.publisher.getEarningsBreakdown(range) });
  return (
    <Card title="By site and slot" subtitle="Captures in the window, by where that money is now. Real (billed) activity only.">
      <QueryView query={q} loadingLabel="Loading earnings by slot" lines={4}>
        {(b: EarningsBreakdown) =>
          b.rows.length === 0 ? (
            <EmptyState icon="chart" title="No earnings in this window" />
          ) : (
            <Table
              caption={`Captured ${formatLocal(b.from)} to ${formatLocal(b.to)}, as of ${asOf(b.as_of)}`}
              rows={b.rows}
              rowKey={(r) => `${r.slot_id}-${r.currency}`}
              columns={[
                { key: "site", header: "Site", cell: (r) => <Link to={`/publisher/sites/${r.site_id}`}>{siteName(r.site_id)}</Link> },
                { key: "tool", header: "Slot", cell: (r) => <code>{r.tool_name}</code> },
                { key: "model", header: "Pricing", cell: (r) => r.pricing_model.toUpperCase() },
                { key: "n", header: "Captures", cell: (r) => r.captures, numeric: true },
                { key: "pend", header: "Pending", cell: (r) => <Money value={r.pending} currency={r.currency} alwaysFlag />, numeric: true },
                { key: "avail", header: "Recognized, available", cell: (r) => <Money value={r.recognized_available} currency={r.currency} />, numeric: true },
                { key: "claimed", header: "Recognized, claimed", cell: (r) => <Money value={r.recognized_claimed} currency={r.currency} />, numeric: true },
                { key: "held", header: "Held", cell: (r) => <Money value={r.compliance_held} currency={r.currency} />, numeric: true },
                { key: "rev", header: "Reversed", cell: (r) => <Money value={r.reversed} currency={r.currency} />, numeric: true },
              ]}
            />
          )
        }
      </QueryView>
    </Card>
  );
}

function ShadowCard({ range, siteName }: { range: Range; siteName: (id: string) => string }) {
  const ep = useEndpoints();
  const q = useQuery({ queryKey: ["earnings-shadow", range], queryFn: () => ep.publisher.getEarningsShadow(range) });
  return (
    <Card
      title="CPE evaluation mode"
      subtitle="Its own section with its own totals: never included in the balances or table above, your statements or payouts."
    >
      <QueryView query={q} loadingLabel="Loading evaluation-mode activity" lines={3}>
        {(s: EarningsShadow) => (
          <div className="stack">
            <Banner tone="info" icon="flask" title="Not paid and not final">
              While CPE billing is in evaluation mode, engagement activity is recorded but earns nothing. Activity recorded in evaluation mode
              will not be billed or earn revenue, even retroactively.
            </Banner>
            {s.real_cpe.length ? (
              <ul className="stack-sm" style={{ margin: 0, paddingLeft: "1.25rem" }}>
                {s.real_cpe.map((r) => (
                  <li key={r.site_id}>
                    <StatusPill tone="success">Real CPE billing now active</StatusPill> on {siteName(r.site_id)} since{" "}
                    {formatLocal(r.real_cpe_active_since)}
                  </li>
                ))}
              </ul>
            ) : null}
            {s.by_site.length === 0 ? (
              <p className="muted">No evaluation-mode activity in this window.</p>
            ) : (
              <Table
                caption="Evaluation-mode activity by site"
                rows={s.by_site}
                rowKey={(r) => `${r.site_id}-${r.currency}`}
                columns={[
                  { key: "site", header: "Site", cell: (r) => siteName(r.site_id) },
                  { key: "dec", header: "Decisions", cell: (r) => r.decisions, numeric: true },
                  { key: "eng", header: "Engagements", cell: (r) => r.engagements, numeric: true },
                  { key: "pend", header: "Still classifying", cell: (r) => r.engagements_pending, numeric: true },
                  {
                    key: "proj",
                    header: "Projected gross (not paid)",
                    cell: (r) => <Money value={r.projected_gross} currency={r.currency} alwaysFlag />,
                    numeric: true,
                  },
                ]}
              />
            )}
            <p className="xs muted">
              Projected gross is the advertiser price before the platform fee, not what you'd have earned. When evaluation mode ended isn't
              recorded yet.
            </p>
          </div>
        )}
      </QueryView>
    </Card>
  );
}
